import { FileText } from 'lucide-react';

import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { OrigemBadge } from '@/components/ui/OrigemBadge';

import { TagsDoRegistro } from '@/components/tags/TagsDoRegistro';
import { useTagsNoCache } from '@/hooks/useTagsNoCache';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { ModeloActionsMenu } from './ModeloActionsMenu';
import type { PeticaoModelo } from './ModelosPage';
import { extractPlainText } from './ModelosPage';

interface ModeloTableProps {
  modelos: PeticaoModelo[];
  canWrite: (origem?: string | null, escritorioId?: string | null) => boolean;
  canShare: (origem?: string | null, escritorioId?: string | null, userId?: string | null) => boolean;
  onUsar: (modelo: PeticaoModelo) => void;
  onView: (modelo: PeticaoModelo) => void;
  onEdit: (modelo: PeticaoModelo) => void;
  onDuplicate: (modelo: PeticaoModelo) => void;
  onShare: (modelo: PeticaoModelo) => void;
  onDelete: (modelo: PeticaoModelo) => void;
  /** O modelo cujo diálogo de tags está aberto (nulo ao fechar). */
  onTagsAbertas?: (id: string | null) => void;
}

const CACHES_DE_MODELO = [queryKeys.modelos.all] as const;

export function ModeloTable({
  modelos,
  canWrite,
  canShare,
  onUsar,
  onView,
  onEdit,
  onDuplicate,
  onShare,
  onDelete,
  onTagsAbertas,
}: ModeloTableProps) {
  const tagsNoCache = useTagsNoCache(CACHES_DE_MODELO);
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Título</TableHead>
            <TableHead className="hidden sm:table-cell">Preview</TableHead>
            <TableHead className="hidden sm:table-cell">Data</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead className="text-right w-[80px]">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {modelos.map((modelo) => (
            <TableRow
              key={modelo.id}
              className="cursor-pointer"
              onClick={() => onView(modelo)}
            >
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="block break-words text-sm font-semibold">
                      {modelo.titulo}
                    </span>
                    {modelo.origem && modelo.origem !== 'pessoal' && (
                      <div className="mt-0.5">
                        <OrigemBadge item={modelo} />
                      </div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <span className="text-sm text-muted-foreground line-clamp-1">
                  {extractPlainText(modelo.corpo) || 'Modelo sem conteúdo'}
                </span>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {modelo.created_at
                    ? new Date(modelo.created_at).toLocaleDateString('pt-BR')
                    : '\u2014'}
                </span>
              </TableCell>
              <TableCell>
                <TagsDoRegistro
                  recurso="modelos"
                  registroId={modelo.id}
                  identificador={modelo.titulo}
                  tags={modelo.tags}
                  noCache={tagsNoCache}
                  onAbertoChange={(aberto) => onTagsAbertas?.(aberto ? modelo.id : null)}
                  max={2}
                  compacto
                />
              </TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <ModeloActionsMenu
                  modelo={modelo}
                  canWrite={canWrite}
                  canShare={canShare}
                  onUsar={onUsar}
                  onView={onView}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onShare={onShare}
                  onDelete={onDelete}
                  triggerClassName="h-8 w-8"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
